/**
 * @fileoverview App-level Redux selectors for accessing application state.
 * 
 * These selectors provide access to:
 * - Initialization status
 * - CSA data (all types)
 * - Counterparty lists (derived from CSA data)
 * - End-of-day (EOD) date information
 * 
 * @module app/selectors
 */

import fp from 'lodash/fp';

import { createCounterpartyList } from '../api/adapters/filterAdapters';

/**
 * Gets the application initialization status.
 * 
 * @function
 * @param {Object} state - Redux state
 * @returns {boolean} Whether the app has completed initialization
 * 
 * @example
 * const initialised = useSelector(getInitialised);
 * if (!initialised) return <Spinner />;
 */
export const getInitialised = fp.get('app.initialised');

/**
 * Gets all CSA data (all types).
 * 
 * @function
 * @param {Object} state - Redux state
 * @returns {Array<Object>} Array of CSA objects
 * @returns {string} [].csa - CSA identifier
 * @returns {string} [].counterparty - Counterparty name
 * @returns {string} [].type - CSA type (IM or Monitoring)
 * 
 * @example
 * const csas = useSelector(getCsas);
 * // Returns: [{ csa: 'CSA001', counterparty: 'ABC Corp', type: 'IM' }, ...]
 */
export const getCsas = fp.get('app.csaData');

/**
 * Gets a list of all counterparties (derived from CSA data).
 * 
 * This selector extracts unique counterparty names from the CSA list,
 * sorts them alphabetically, and returns them as a simple array.
 * 
 * @function
 * @param {Object} state - Redux state
 * @returns {Array<string>} Sorted array of unique counterparty names
 * 
 * @example
 * const counterparties = useSelector(getCounterparties);
 * // Returns: ['ABC Corp', 'XYZ Bank', ...]
 */
export const getCounterparties = fp.flow(
    fp.get('app.csaData'),
    createCounterpartyList
);

/**
 * Gets all monitoring CSA data.
 * 
 * @function
 * @param {Object} state - Redux state
 * @returns {Array<Object>} Array of monitoring CSA objects
 * 
 * @example
 * const monitorCsas = useSelector(getMonitorCsas);
 */
export const getMonitorCsas = fp.get('app.monitoringCsaData');

/**
 * Gets a list of counterparties from monitoring CSAs.
 * 
 * @function
 * @param {Object} state - Redux state
 * @returns {Array<string>} Sorted array of unique counterparty names from monitoring CSAs
 * 
 * @example
 * const monitorCounterparties = useSelector(getMonitorCounterparties);
 */
export const getMonitorCounterparties = fp.flow(
    fp.get('app.monitoringCsaData'),
    createCounterpartyList
);

/**
 * Gets all IM (Initial Margin) CSA data.
 * 
 * @function
 * @param {Object} state - Redux state
 * @returns {Array<Object>} Array of IM CSA objects
 * 
 * @example
 * const imCsas = useSelector(getImCsas);
 */
export const getImCsas = fp.get('app.imCsaData');

/**
 * Gets a list of counterparties from IM CSAs.
 * 
 * @function
 * @param {Object} state - Redux state
 * @returns {Array<string>} Sorted array of unique counterparty names from IM CSAs
 * 
 * @example
 * const imCounterparties = useSelector(getImCounterparties);
 */
export const getImCounterparties = fp.flow(
    fp.get('app.imCsaData'),
    createCounterpartyList
);

/**
 * Gets the available end-of-day (EOD) date range.
 * 
 * @function
 * @param {Object} state - Redux state
 * @returns {Object} EOD date information
 * @returns {string} from - Start date (YYYY-MM-DD format)
 * @returns {string} to - End date (YYYY-MM-DD format)
 * @returns {boolean} today - Whether EOD is today's date
 * 
 * @example
 * const eod = useSelector(getAvailableEOD);
 * // Returns: { from: '2025-10-22', to: '2025-10-22', today: false }
 */
export const getAvailableEOD = fp.get('app.EOD');
